/**
 * Listing view — renders `#listingTable` using the mockup's exact 10-column
 * `.listing-table` (vertailutyokalu.html:10120). Anonymous (level=0) users
 * see only Maakunta and Pyyntihinta clearly; every other column is blurred
 * with a placeholder so the visual matches the mockup gate.
 *
 * Filters: region (debounced 250 ms, sent to backend), numeric filters and
 * sort are client-side over the fetched page. Column labels come from i18n.
 */
import { listEstates } from '../api/estates.ts';
import { bindSavedSearchControls } from './saved-searches.ts';
import { currentUser } from '../state/session.ts';
import { t, tn, getLang } from '../i18n/index.ts';
import { REGIONS } from '../data/regions.ts';
import type { AnonEstate, Estate, EstateFilter } from '../types/api.ts';

function eurFmt(): Intl.NumberFormat {
  return new Intl.NumberFormat(getLang() === 'en' ? 'en-FI' : 'fi-FI', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}
function numFmt(d = 0): Intl.NumberFormat {
  return new Intl.NumberFormat(getLang() === 'en' ? 'en-FI' : 'fi-FI', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

function fmtEUR(v: number | undefined | null): string {
  if (v == null || !isFinite(v) || v === 0) return '—';
  return eurFmt().format(v);
}
function fmtN(v: number | undefined | null, d = 0): string {
  if (v == null || !isFinite(v) || v === 0) return '—';
  return numFmt(d).format(v);
}
function fmtDate(v: string | undefined | null): string {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
  } catch {
    return v;
  }
}

interface ListingState {
  rows: Array<Estate | AnonEstate>;
  // Audit v2 virhe 4 (2026-05-30): the single-region select became a
  // multi-select. We track the selection as a string[] (preserves insertion
  // order for the label) and apply it client-side in paint() — keeps
  // saved-searches backwards-compatible (a single string is treated as a
  // one-element list on load).
  regions: string[];
  minHa: number;
  minPrice: number;
  minM3: number;
  sort: string;
  // Reported 2026-05-31: level≥3 / admin can switch between currently-
  // for-sale and toteutuneet kaupat. Empty string = both buckets (the
  // default for level≥3). Lower tiers always see only active rows
  // regardless of this state.
  saleStatus: '' | 'active' | 'closed';
}

const state: ListingState = {
  rows: [],
  regions: [],
  minHa: 0,
  minPrice: 0,
  minM3: 0,
  sort: 'deadline',
  saleStatus: '',
};

export interface ListingOptions {
  onSelect: (estateId: number) => void;
}

let opts: ListingOptions = { onSelect: () => {} };

/** Returns the currently-loaded listing rows for use by the search box. */
export function getLoadedRows(): Array<Estate | AnonEstate> {
  return state.rows;
}

export function bindListing(o: ListingOptions): void {
  opts = o;

  const regionWrap = document.getElementById('filterRegion') as HTMLDivElement | null;
  const regionDropdown = document.getElementById('filterRegionDropdown') as HTMLDivElement | null;
  const minHa = document.getElementById('filterMinHa') as HTMLInputElement;
  const minPrice = document.getElementById('filterMinPrice') as HTMLInputElement;
  const minM3 = document.getElementById('filterMinM3') as HTMLInputElement;
  const sort = document.getElementById('filterSort') as HTMLSelectElement;
  const reset = document.getElementById('btnReset') as HTMLButtonElement;
  // Sale-status toggle (level≥3 / admin only). The filter group is
  // hidden in index.html and only revealed when the caller's session
  // permits viewing toteutuneet kaupat — see canSeeClosedSales.
  const saleStatus = document.getElementById('filterSaleStatus') as HTMLSelectElement | null;
  const saleStatusGroup = document.getElementById('filterSaleStatusGroup') as HTMLDivElement | null;
  if (saleStatus && saleStatusGroup) {
    if (canSeeClosedSales()) {
      saleStatusGroup.style.display = '';
    }
    saleStatus.addEventListener('change', () => {
      const v = saleStatus.value;
      state.saleStatus = v === 'active' || v === 'closed' ? v : '';
      void load();
    });
  }

  // Populate the multi-select dropdown once. The widget itself is a
  // checkbox-list copy of the pattern used in market.ts so behaviour stays
  // consistent between the two filter bars.
  if (regionWrap && regionDropdown) {
    populateRegionDropdown(regionDropdown);
    regionWrap.addEventListener('click', (e) => {
      e.stopPropagation();
      const isOpen = regionDropdown.style.display !== 'none';
      regionDropdown.style.display = isOpen ? 'none' : 'block';
    });
    document.addEventListener('click', () => {
      regionDropdown.style.display = 'none';
    });
  }

  for (const el of [minHa, minPrice, minM3] as HTMLInputElement[]) {
    el.addEventListener('input', () => {
      state.minHa = Number(minHa.value) || 0;
      state.minPrice = Number(minPrice.value) || 0;
      state.minM3 = Number(minM3.value) || 0;
      paint();
    });
  }
  sort.addEventListener('change', () => {
    state.sort = sort.value;
    paint();
  });

  reset.addEventListener('click', () => {
    minHa.value = '';
    minPrice.value = '';
    minM3.value = '';
    sort.value = 'deadline';
    if (saleStatus) saleStatus.value = '';
    state.regions = [];
    state.minHa = 0;
    state.minPrice = 0;
    state.minM3 = 0;
    state.sort = 'deadline';
    state.saleStatus = '';
    if (regionDropdown) {
      regionDropdown
        .querySelectorAll<HTMLInputElement>('input[type=checkbox]')
        .forEach((c) => {
          c.checked = false;
        });
      refreshRegionLabel();
    }
    void load();
  });

  // M6: saved-searches dropdown + "Tallenna haku" button. The bar is a
  // sibling of the filter bar so it reflows nicely on narrow viewports.
  // For anonymous visitors `bindSavedSearchControls` becomes a no-op.
  let savedHost = document.getElementById('savedSearchesBar') as HTMLElement | null;
  if (!savedHost) {
    savedHost = document.createElement('div');
    savedHost.id = 'savedSearchesBar';
    const filterBar = document.querySelector('.filter-bar');
    if (filterBar && filterBar.parentElement) {
      filterBar.parentElement.insertBefore(savedHost, filterBar.nextSibling);
    }
  }
  void bindSavedSearchControls(savedHost, {
    // Audit v2 virhe 4 (2026-05-30): saved-search payload now carries
    // `regions: string[]`. The single `region: string` is preserved for
    // backwards compatibility with rows saved before this release —
    // applyFilters below normalises both shapes back into state.regions.
    currentFilters: () => ({
      regions: state.regions.length > 0 ? state.regions.slice() : undefined,
      ha_min: state.minHa || undefined,
      price_min: state.minPrice || undefined,
      m3_min: state.minM3 || undefined,
      sort: state.sort,
    }),
    applyFilters: (filters) => {
      const f = filters as Partial<{
        region: string;
        regions: string[];
        ha_min: number;
        price_min: number;
        m3_min: number;
        sort: string;
      }>;
      const next: string[] = Array.isArray(f.regions)
        ? f.regions.filter((r): r is string => typeof r === 'string' && r !== '')
        : f.region
          ? [f.region]
          : [];
      state.regions = next;
      minHa.value = f.ha_min != null ? String(f.ha_min) : '';
      minPrice.value = f.price_min != null ? String(f.price_min) : '';
      minM3.value = f.m3_min != null ? String(f.m3_min) : '';
      sort.value = f.sort ?? 'deadline';
      state.minHa = Number(minHa.value) || 0;
      state.minPrice = Number(minPrice.value) || 0;
      state.minM3 = Number(minM3.value) || 0;
      state.sort = sort.value;
      if (regionDropdown) {
        regionDropdown
          .querySelectorAll<HTMLInputElement>('input[type=checkbox]')
          .forEach((c) => {
            c.checked = state.regions.includes(c.value);
          });
        refreshRegionLabel();
      }
      void load();
    },
  });
}

export async function load(): Promise<void> {
  refreshSubtitle();
  const tbl = document.getElementById('listingTable')!;
  const count = document.getElementById('listingCount')!;
  tbl.innerHTML = `<div class="listing-empty">${escapeHtml(t('listing_empty'))}</div>`;
  count.textContent = '';

  // The backend filter is single-region-only; for multi-region selections
  // we fetch with no region and apply the union client-side in paint().
  // For exactly one region we still pass it down so the server can use its
  // index. For level=1 callers the backend region-scopes the response
  // anyway, so the union is a no-op there.
  const filter: EstateFilter = { limit: 500, page: 1 };
  if (state.regions.length === 1) filter.region = state.regions[0];
  if (state.saleStatus) filter.sale_status = state.saleStatus;
  try {
    const resp = await listEstates(filter);
    state.rows = resp.data || [];
    paint();
  } catch (err) {
    tbl.innerHTML = `<div class="listing-empty" style="color:rgb(140,55,55)">${escapeHtml(String((err as Error).message))}</div>`;
  }
}

// Audit v2 virhe 4 (2026-05-30): the multi-select widget is identical in
// shape to the one in market.ts. We keep a private copy here (rather than
// extracting to a shared module) because listing.ts is performance-sensitive
// during initial load and we want to avoid an extra import boundary in the
// hot path; the two implementations are intentionally short and parallel.
function populateRegionDropdown(dd: HTMLDivElement): void {
  // Level=1 users are region-scoped: only their allowed_regions are
  // selectable (asiakaspalaute 3.6.2026 — picking a disallowed region used
  // to 403 the whole listing, and the backend only ever returned the first
  // allowed region). Everyone else (level≥2 / admin / institution) sees all
  // maakunnat. allowed_regions is empty/undefined for non-level-1 accounts.
  const u = currentUser();
  const scoped = u && u.role !== 'admin' && u.level === 1 && (u.allowed_regions?.length ?? 0) > 0;
  const options = scoped
    ? REGIONS.filter((r) => u!.allowed_regions!.includes(r.name))
    : REGIONS;
  dd.innerHTML = options
    .map(
      (r) => `<label class="multi-select-opt">
      <input type="checkbox" value="${escapeHtml(r.name)}"${state.regions.includes(r.name) ? ' checked' : ''}>
      <span>${escapeHtml(r.name)}</span>
    </label>`,
    )
    .join('');
  dd.querySelectorAll<HTMLInputElement>('input[type=checkbox]').forEach((cb) => {
    cb.addEventListener('change', () => {
      state.regions = Array.from(
        dd.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked'),
      ).map((c) => c.value);
      refreshRegionLabel();
      void load();
    });
  });
  refreshRegionLabel();
}

function refreshRegionLabel(): void {
  const txt = document.getElementById('filterRegionText');
  if (!txt) return;
  if (state.regions.length === 0) {
    txt.textContent = t('filter_all');
  } else if (state.regions.length === 1) {
    txt.textContent = state.regions[0];
  } else {
    txt.textContent = t('filter_n_regions', { n: state.regions.length });
  }
}

function paint(): void {
  const tbl = document.getElementById('listingTable')!;
  const count = document.getElementById('listingCount')!;

  let list = state.rows.slice();
  // Multi-region filter: when 2+ regions are selected we fetched the whole
  // page and apply the union here. When 0 or 1 are selected the backend
  // already returned the correct slice, so this is a no-op.
  if (state.regions.length > 1) {
    const set = new Set(state.regions);
    list = list.filter((e) => typeof e.region === 'string' && set.has(e.region));
  }
  if (state.minHa > 0) list = list.filter((e) => (effArea(e) ?? 0) >= state.minHa);
  if (state.minPrice > 0) list = list.filter((e) => (e.price ?? 0) >= state.minPrice);
  if (state.minM3 > 0) list = list.filter((e) => m3PerHa(e) >= state.minM3);
  list.sort(sortFn(state.sort));

  count.textContent = tn('listing_count', list.length);

  if (list.length === 0) {
    tbl.innerHTML = `<div class="listing-empty">${escapeHtml(t('listing_empty'))}</div>`;
    return;
  }

  const anon = isAnonymousCaller();

  tbl.innerHTML = `
    <div class="listing-table-wrap">
    <table class="listing-table">
      <thead>
        <tr>
          <th>${escapeHtml(t('th_id'))}</th>
          <th>${escapeHtml(t('th_name'))}</th>
          <th>${escapeHtml(t('th_municipality'))}</th>
          <th>${escapeHtml(t('th_region'))}</th>
          <th class="num">${escapeHtml(t('th_total_ha'))}</th>
          <th class="num">${escapeHtml(t('th_total_m3'))}</th>
          <th class="num">${escapeHtml(t('th_m3ha_short'))}</th>
          <th class="num">${escapeHtml(t('th_eurha_short'))}</th>
          <th class="num">${escapeHtml(t('th_price'))}</th>
          <th class="num">${escapeHtml(t('th_deadline'))}</th>
        </tr>
      </thead>
      <tbody>
        ${list.map((e) => row(e, anon)).join('')}
      </tbody>
    </table>
    </div>
  `;

  tbl.querySelectorAll<HTMLElement>('tr[data-id]').forEach((tr) => {
    tr.addEventListener('click', () => {
      const id = Number(tr.dataset.id);
      if (Number.isFinite(id)) opts.onSelect(id);
    });
  });
}

/**
 * Render a single row. Mockup blur pattern (vertailutyokalu.html:10140):
 *   - blurred for anonymous: TUNNUS, TILAN NIMI, KUNTA, PINTA-ALA, PUUSTO,
 *     M³/HA, €/HA, DEADLINE
 *   - always clear: MAAKUNTA, PYYNTIHINTA
 *
 * Backend's level=0 projection omits municipality / timber_volume / closing
 * _date / property_id, so for anonymous callers we synthesise stable
 * placeholder strings (deterministic by id) that get visually blurred.
 * Users with a session see the real backend values without blur.
 */
function row(e: Estate | AnonEstate, anon: boolean): string {
  const full = e as Estate;
  const area = effArea(e);
  const m3ha = m3PerHa(e);
  const totalM3 = full.timber_volume;
  const eurHa = area && full.price ? full.price / area : null;
  const propId = (full as Estate & { property_id?: string }).property_id || `#${e.id}`;

  const blur = anon ? ' blurred' : '';

  // Anonymous placeholders — deterministic look-alike values per id so the
  // blur has substance. The user never sees these unblurred because the
  // CSS `filter: blur(5px)` makes them illegible. The actual data is also
  // not present (backend redacts), so a determined user inspecting the DOM
  // still only sees these decoys.
  const aId = anon ? fakeId(e.id) : propId;
  const aName = anon ? fakeName(e.id) : (full.name || '—');
  const aMunicipality = anon ? fakeMunicipality(e.id) : (full.municipality || '—');
  const aArea = anon ? `${numFmt(1).format(10 + (e.id % 60))}` : fmtN(area, 1);
  const aTimber = anon ? `${numFmt(0).format(500 + ((e.id * 17) % 9000))}` : fmtN(totalM3);
  const aM3ha = anon ? `${numFmt(0).format(80 + (e.id % 80))}` : fmtN(m3ha);
  const aEurHa = anon ? `${numFmt(0).format(3000 + ((e.id * 7) % 6000))}` : fmtN(eurHa);
  const aDeadline = anon ? fakeDeadline(e.id) : fmtDate(full.closing_date);

  return `
    <tr data-id="${e.id}" tabindex="0">
      <td class="id${blur}">${escapeHtml(aId)}</td>
      <td class="name${blur}" title="${escapeHtml(anon ? '' : full.name || '')}">${escapeHtml(aName)}</td>
      <td class="${anon ? 'blurred' : ''}">${escapeHtml(aMunicipality)}</td>
      <td>${escapeHtml((full.region as string) || (e as AnonEstate).region || '—')}</td>
      <td class="num${blur}">${aArea}</td>
      <td class="num${blur}">${aTimber}</td>
      <td class="num${blur}">${aM3ha}</td>
      <td class="num${blur}">${aEurHa}</td>
      <td class="num">${fmtEUR(full.price ?? (e as AnonEstate).price)}</td>
      <td class="num deadline${blur}">${aDeadline}</td>
    </tr>
  `;
}

function effArea(e: Estate | AnonEstate): number | undefined {
  // AnonEstate intentionally has no area field — backend redacts it for
  // level=0 callers (see types/api.ts). The cast is safe because the
  // resulting undefined just falls through to the blur placeholder.
  const f = e as Estate;
  return f.forest_area || f.area;
}
function m3PerHa(e: Estate | AnonEstate): number {
  const f = e as Estate;
  const a = effArea(e) || 0;
  if (!a || !f.timber_volume) return 0;
  return f.timber_volume / a;
}

function isAnonymousCaller(): boolean {
  return !currentUser();
}

// canSeeClosedSales mirrors smt_estate_handler.go's "level≥3 / admin"
// gate: only those tiers receive toteutuneet kaupat alongside live
// listings, and therefore only those tiers see the "Vain myynnissä /
// Toteutuneet" filter dropdown. Institutions are not included — they
// are treated as the level=2 "Pro" tier and only see currently-for-sale.
function canSeeClosedSales(): boolean {
  const u = currentUser();
  if (!u) return false;
  if (u.role === 'admin') return true;
  return (u.level ?? 0) >= 3;
}

function refreshSubtitle(): void {
  const sub = document.getElementById('listingSub');
  if (!sub) return;
  // Mockup (vertailutyokalu.html:4198) shows one subtitle for everyone —
  // "Metsätilat, joissa on tarjousaikaa vielä jäljellä". The earlier
  // anonymous-only variant was not in the reference; removed 3.6.2026.
  sub.textContent = t('listing_sub');
}

function sortFn(key: string): (a: Estate | AnonEstate, b: Estate | AnonEstate) => number {
  switch (key) {
    case 'ha_desc':
      return (a, b) => (effArea(b) || 0) - (effArea(a) || 0);
    case 'ha_asc':
      return (a, b) => (effArea(a) || 0) - (effArea(b) || 0);
    case 'price_desc':
      return (a, b) => (b.price || 0) - (a.price || 0);
    case 'price_asc':
      return (a, b) => (a.price || 0) - (b.price || 0);
    case 'm3ha_desc':
      return (a, b) => m3PerHa(b) - m3PerHa(a);
    case 'eurha_asc': {
      const eha = (e: Estate | AnonEstate): number => {
        const a = effArea(e) || 0;
        const p = e.price || 0;
        return a > 0 ? p / a : 0;
      };
      return (a, b) => eha(a) - eha(b);
    }
    case 'eurha_desc': {
      const eha = (e: Estate | AnonEstate): number => {
        const a = effArea(e) || 0;
        const p = e.price || 0;
        return a > 0 ? p / a : 0;
      };
      return (a, b) => eha(b) - eha(a);
    }
    case 'deadline':
    default:
      return (a, b) => {
        const da = (a as Estate).closing_date;
        const db = (b as Estate).closing_date;
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return new Date(da).getTime() - new Date(db).getTime();
      };
  }
}

// --- Anonymous placeholders --------------------------------------------------

function fakeId(seed: number): string {
  // Pattern that visually resembles a kiinteistötunnus like "305-408-6-6".
  const a = 100 + (seed % 800);
  const b = 100 + ((seed * 7) % 800);
  const c = 1 + (seed % 30);
  const d = 1 + ((seed * 3) % 60);
  return `${a}-${b}-${c}-${d}`;
}
function fakeName(seed: number): string {
  const stems = ['Honkavaara', 'Mäntylä', 'Lehtoniemi', 'Korpisalmi', 'Rantamäki', 'Kuusela', 'Niittymaa', 'Saarela'];
  return stems[seed % stems.length] + ' ' + (10 + (seed % 90));
}
function fakeMunicipality(seed: number): string {
  const m = ['Kuusamo', 'Joensuu', 'Rovaniemi', 'Kajaani', 'Mikkeli', 'Kotka', 'Lahti', 'Pori'];
  return m[seed % m.length];
}
function fakeDeadline(seed: number): string {
  const days = (seed * 13) % 365;
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString(getLang() === 'en' ? 'en-GB' : 'fi-FI');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
